import React from 'react';
import { Edit3, Headphones, Play, Timer, Copy, Check, Globe } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { toast } from 'sonner';
import axios from 'axios';

const API = (process.env.REACT_APP_BACKEND_URL || '') + '/api';

const LOCALE_OPTIONS = [
  { code: 'en', flag: '🇬🇧', name: 'English' },
  { code: 'fr', flag: '🇫🇷', name: 'Français' },
  { code: 'de', flag: '🇩🇪', name: 'Deutsch' },
  { code: 'es', flag: '🇪🇸', name: 'Español' },
  { code: 'pt', flag: '🇧🇷', name: 'Português' },
  { code: 'zh', flag: '🇨🇳', name: '中文' },
];

const statusMeta = {
  published: ['Published', 'bg-emerald-50 text-emerald-700 border-emerald-200'], approved: ['Approved', 'bg-teal-50 text-teal-700 border-teal-200'], ready_for_review: ['Ready for review', 'bg-sky-50 text-sky-700 border-sky-200'], ready: ['Ready', 'bg-emerald-50 text-emerald-700 border-emerald-200'], draft: ['Draft', 'bg-secondary text-muted-foreground border-border'], processing: ['Processing', 'bg-amber-50 text-amber-700 border-amber-200'], queued: ['Queued', 'bg-amber-50 text-amber-700 border-amber-200'], archived: ['Archived', 'bg-zinc-100 text-zinc-600 border-zinc-300'], failed: ['Failed', 'bg-rose-50 text-rose-700 border-rose-200'], production_required: ['Production required', 'bg-secondary text-muted-foreground border-border'],
};

const duration = (seconds) => {
  if (!Number.isFinite(Number(seconds)) || Number(seconds) <= 0) return 'Duration not set';
  const min = Math.floor(Number(seconds) / 60); const sec = Number(seconds) % 60;
  return `${min}:${String(sec).padStart(2, '0')}`;
};

export const MediaCard = ({ item, index, assetUrl, onOpen, onEdit }) => {
  const [copied, setCopied] = React.useState(false);
  const [assignLocale, setAssignLocale] = React.useState('');
  const [assigning, setAssigning] = React.useState(false);

  const effective = item.processing_status === 'processing' ? 'processing' : item.status;
  const [label, tone] = statusMeta[effective] || statusMeta.draft;
  const playable = Boolean(item.url) && item.processing_status === 'ready';
  // Feb-2026 · Lets anyone grab a stable reference for this exact asset
  // when asking for it to be placed somewhere — a short slug for quick
  // typing, backed by a one-click copy of the actual public asset URL.
  const publicUrl = item.url ? assetUrl(item.url) : '';
  const copyUrl = async () => {
    try {
      await navigator.clipboard.writeText(publicUrl || item.slug);
      setCopied(true);
      toast.success('Copied — paste this to point to this exact media item.');
      setTimeout(() => setCopied(false), 1600);
    } catch { toast.error('Could not copy to clipboard.'); }
  };

  const assignToLocale = async () => {
    if (!assignLocale) return;
    if (!item.slug) { toast.error('Media item has no slug — cannot assign.'); return; }
    setAssigning(true);
    try {
      await axios.post(
        `${API}/admin/landing/locale-video-from-media`,
        { locale: assignLocale, media_slug: item.slug },
        { withCredentials: true },
      );
      const localeMeta = LOCALE_OPTIONS.find(l => l.code === assignLocale);
      toast.success(`"${item.title}" set as Mei intro video for ${localeMeta?.name ?? assignLocale} landing page.`);
      setAssignLocale('');
    } catch (e) {
      toast.error('Assignment failed.', { description: e?.response?.data?.detail || 'Check the backend logs.' });
    }
    setAssigning(false);
  };
  return (
    <article className="group overflow-hidden border border-border bg-card" data-testid={`media-item-${item.slug}`}>
      <div className="relative aspect-video overflow-hidden bg-[#0c1f1c]">
        {item.thumbnail_url ? <img src={assetUrl(item.thumbnail_url)} alt="" className="h-full w-full object-contain" data-testid={`media-thumbnail-${item.slug}`} /> : <div className="absolute inset-0 opacity-30 [background-image:linear-gradient(rgba(255,255,255,.08)_1px,transparent_1px),linear-gradient(90deg,rgba(255,255,255,.08)_1px,transparent_1px)] [background-size:28px_28px]" />}
        <button type="button" onClick={() => playable && onOpen(item)} disabled={!playable} className="absolute inset-0 grid place-items-center text-white disabled:cursor-default" aria-label={playable ? `Preview ${item.title}` : `${item.title} is not ready`} data-testid={`media-open-${item.slug}`}>{item.media_kind === 'audio' ? <Headphones className="h-9 w-9 text-amber-200" /> : playable ? <Play className="h-10 w-10 transition-transform group-hover:scale-110" /> : <Timer className="h-9 w-9 text-amber-200" />}</button>
        <span className="absolute left-3 top-3 bg-black/45 px-1.5 py-0.5 font-mono text-[9px] text-white/80">MEDIA {String(index + 1).padStart(2, '0')}</span>
        {item.processing_status === 'processing' && <div className="absolute inset-x-0 bottom-0 h-1.5 bg-black/40"><div className="h-full bg-amber-400 transition-[width]" style={{ width: `${item.processing_progress || 5}%` }} /></div>}
      </div>
      <div className="p-4">
        <div className="flex items-start justify-between gap-3"><span className={`inline-flex border px-1.5 py-0.5 text-[9px] font-semibold uppercase ${tone}`} data-testid={`media-status-${item.slug}`}>{label}</span><Button variant="ghost" size="icon" onClick={() => onEdit(item)} className="h-7 w-7" aria-label={`Edit ${item.title}`} data-testid={`media-edit-${item.slug}`}><Edit3 className="h-3.5 w-3.5" /></Button></div>
        <h2 className="mt-2 text-base font-semibold text-foreground" data-testid={`media-title-${item.slug}`}>{item.title}</h2>
        <p className="mt-1 line-clamp-3 min-h-[3.75rem] text-xs leading-5 text-muted-foreground" data-testid={`media-description-${item.slug}`}>{item.description || 'No description added.'}</p>
        <div className="mt-3 flex flex-wrap gap-x-3 gap-y-1 text-[9.5px] uppercase text-muted-foreground"><span>{item.category}</span><span>{duration(item.duration_seconds)}</span><span>{item.audience}</span></div>
        <button
          type="button"
          onClick={copyUrl}
          title="Copy a reference you can paste into chat to point to this exact media item"
          data-testid={`media-url-id-${item.slug}`}
          className="mt-2 flex w-full items-center gap-1.5 truncate border border-dashed border-border bg-secondary/40 px-1.5 py-1 font-mono text-[10px] text-muted-foreground transition hover:border-primary hover:text-primary"
        >
          {copied ? <Check className="h-3 w-3 shrink-0 text-emerald-600" /> : <Copy className="h-3 w-3 shrink-0" />}
          <span className="truncate">{item.slug}</span>
        </button>
        <div className="mt-3 border-t border-border pt-2 text-[10px] text-muted-foreground" data-testid={`media-visibility-${item.slug}`}>{[item.published_to_landing && 'Landing page', item.published_to_training && 'Training page'].filter(Boolean).join(' · ') || 'Private media library'}</div>

        {/* 2026-02 · Assign this video to a locale landing page */}
        {item.media_kind !== 'audio' && (
          <div className="mt-3 border-t border-border pt-2">
            <div className="flex items-center gap-1.5">
              <Globe className="h-3 w-3 shrink-0 text-muted-foreground" />
              <span className="text-[10px] font-medium text-muted-foreground">Assign to landing page</span>
            </div>
            <div className="mt-1.5 flex items-center gap-1.5">
              <select
                value={assignLocale}
                onChange={e => setAssignLocale(e.target.value)}
                disabled={assigning}
                className="h-7 flex-1 rounded border border-border bg-background px-1.5 text-[11px] text-foreground focus:outline-none focus:ring-1 focus:ring-primary"
                data-testid={`media-locale-select-${item.slug}`}
              >
                <option value="">— pick locale —</option>
                {LOCALE_OPTIONS.map(l => (
                  <option key={l.code} value={l.code}>{l.flag} {l.name}</option>
                ))}
              </select>
              <Button
                size="sm"
                variant="outline"
                className="h-7 px-2 text-[11px]"
                disabled={!assignLocale || assigning || !item.url}
                onClick={assignToLocale}
                data-testid={`media-locale-assign-${item.slug}`}
                title={!item.url ? 'Video must be processed before assigning' : 'Set as locale landing video'}
              >
                {assigning ? '…' : 'Set'}
              </Button>
            </div>
          </div>
        )}
      </div>
    </article>
  );
};